// ui/components/VisitaForm.jsx  (presentacional: react-hook-form + yup, sin Api ni Swal)
// `children` = secciones extra (imágenes) que se renderizan entre la tarjeta de datos y el pie.
import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ReactHookFormInput } from '../../../../../nuup/componentes/Forms/ReactHookFormInput.jsx';
import CatalogSelect from '../../../shared/ui/CatalogSelect.jsx';
import CatalogAutocomplete from '../../../shared/ui/CatalogAutocomplete.jsx';
import GuardarRegistroButton from '../../../shared/ui/GuardarRegistroButton.jsx';
import PillButton from '../../../shared/ui/PillButton.jsx';
import SectionCard from '../../../shared/ui/SectionCard.jsx';
import { detalleProductor, stringifyProductor } from '../../../shared/domain/productor';
import { visitaSchema, MAX_MOTIVO_VISITA } from '../../domain/visita.schema';

// Si la parcela guardada aún no está en las opciones (cargando, o ya no existe) se agrega para no dejar el Select fuera de rango.
const conParcelaActual = (options, idParcela, loading) =>
  idParcela && !options.some(option => option.id === idParcela)
    ? [
        ...options,
        {
          id: idParcela,
          nombre: loading ? 'Cargando…' : `Parcela ${idParcela}`,
        },
      ]
    : options;

const FIELDSET_STYLE = { border: 0, padding: 0, margin: 0, minWidth: 0 };

// onSubmit(values, setError): la página mapea los errores del backend sobre los campos con setError.
const VisitaForm = ({ initialValues, catalogos, submitting = false, onSubmit, onCancel, onProductorChange, children }) => {
  const {
    control,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm({ resolver: yupResolver(visitaSchema), mode: 'onBlur', defaultValues: initialValues });

  const idCuenta = watch('id_cuenta');
  const idParcela = watch('id_parcela');

  // Cambiar de productor invalida la parcela elegida.
  const handleProductor = id => {
    setValue('id_parcela', null);
    onProductorChange(id);
  };

  const submit = handleSubmit(values => onSubmit(values, setError));

  return (
    <form onSubmit={submit} noValidate>
      {/* Mientras se guarda, el fieldset deshabilita todos los controles nativos (inputs, botones, file) sin tocar su estilo. */}
      <fieldset disabled={submitting} style={FIELDSET_STYLE}>
        <SectionCard title="Datos de la visita" subtitle="Los campos marcados con * son obligatorios.">
          <div className="row mb-3">
            <div className="col-md-6">
              <ReactHookFormInput type="date" id="fecha" name="fecha" label="Fecha*" control={control} error={errors.fecha} />
            </div>
            <div className="col-md-6">
              <CatalogAutocomplete
                name="id_cuenta"
                label="Productor*"
                control={control}
                options={catalogos.productores}
                loading={catalogos.productoresLoading}
                disabled={submitting}
                placeholder="Busca al productor"
                startIcon="fa-user"
                uppercase
                stringify={stringifyProductor}
                renderOptionSecondary={detalleProductor}
                error={errors.id_cuenta}
                onChangeExternal={handleProductor}
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <ReactHookFormInput
                type="text"
                id="motivo"
                name="motivo"
                label="Motivo*"
                control={control}
                error={errors.motivo}
                maxLength={MAX_MOTIVO_VISITA}
              />
            </div>
            <div className="col-md-6">
              <CatalogSelect
                id="id_parcela"
                name="id_parcela"
                label="Parcela (opcional)"
                control={control}
                options={conParcelaActual(catalogos.parcelas, idParcela, catalogos.parcelasLoading)}
                disabled={!idCuenta || catalogos.parcelasLoading || submitting}
                placeholder={
                  !idCuenta
                    ? 'Selecciona primero un productor'
                    : catalogos.parcelasLoading
                    ? 'Cargando parcelas…'
                    : 'Selecciona una parcela'
                }
                startIcon="fa-map-marker-alt"
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <ReactHookFormInput
                type="text"
                id="observaciones"
                name="observaciones"
                label="Observaciones o recomendaciones"
                control={control}
                multiline
                minRows={3}
              />
            </div>
          </div>
        </SectionCard>

        {children}
      </fieldset>

      <div className="d-flex justify-content-end mb-4" style={{ gap: 10 }}>
        <PillButton onClick={onCancel} disabled={submitting}>
          Cancelar
        </PillButton>
        <GuardarRegistroButton saving={submitting} onClick={submit} />
      </div>
    </form>
  );
};

export default VisitaForm;
